/* ═══════════════════════════════════════════════════════════════
   buildPlanRows — pure row-shaping for the Plan grid. Two pivots share
   one output shape (GroupData[] of ParentRowData[] of PairingRow[]) so
   PlanGrid.tsx renders both identically — "same grid transposed, never
   a different page" (contract).

   Sparse by design (D5): a person/project only appears if they have a
   real assignment or actual hours somewhere in the visible horizon —
   never a full cartesian person × project matrix.
   ═══════════════════════════════════════════════════════════════ */

import type {
  TeamMember,
  Department,
  Team,
  TeamAssignment,
  ProjectAssignment,
  HourForecast,
  Project,
} from '../../../shared/types';
import type { PairingRow, PersonLayout, ProjectBreakdown } from './types';
import { initialsOf, mondayOf, projectColor, VACATION_COLOR } from './utils';

export interface ParentRowData {
  id: string; // `member:${id}` | `project:${id}`
  kind: 'member' | 'project';
  refId: string;
  label: string;
  hint?: string;
  color?: string;
  pairings: PairingRow[];
}

export interface GroupData {
  id: string;
  label: string;
  color?: string;
  parents: ParentRowData[];
}

/** Sum of a pairing's hours, bucketed by the week its day_date belongs to.
 *  Weeks are exactly 7 days apart and Monday-anchored, so `mondayOf(day)`
 *  IS the week key — no range-matching needed. */
function bucketByWeek(rows: HourForecast[]): Map<string, HourForecast[]> {
  const byWeek = new Map<string, HourForecast[]>();
  for (const f of rows) {
    const day = f.dayDate || f.weekStartDate;
    if (!day) continue;
    const wk = mondayOf(day);
    const arr = byWeek.get(wk) || [];
    arr.push(f);
    byWeek.set(wk, arr);
  }
  return byWeek;
}

function sumHours(rows: HourForecast[] | undefined): number {
  if (!rows) return 0;
  let s = 0;
  for (const f of rows) s += Number(f.hours) || 0;
  return s;
}

// ─── Project scoping (T-PLUMB — forecasting2 embedded in a project) ──

/** The two data shapes that carry a `projectId` and so are the ones a
 *  project-scoped forecasting embed needs to narrow before the pivot
 *  builders run. `projectAssignments` IS the member↔project pairing —
 *  `teamAssignments` (member↔team) never carries a projectId and is left
 *  alone. */
export interface ProjectScopable {
  forecasts: HourForecast[];
  projectAssignments: ProjectAssignment[];
}

/**
 * Narrows forecasts + project assignments to one project for an
 * embedded, single-project forecasting view. Vacation forecast rows never
 * carry a projectId (or matching sourceKind) so they always fall out.
 * With no `projectId`, `inputs` comes back untouched — the firm-wide
 * page's behavior is byte-for-byte unchanged.
 */
export function scopeToProject<T extends ProjectScopable>(
  inputs: T,
  projectId: string | undefined
): T {
  if (!projectId) return inputs;
  return {
    ...inputs,
    forecasts: inputs.forecasts.filter(
      (f) => f.projectId === projectId && f.sourceKind !== 'vacation'
    ),
    projectAssignments: inputs.projectAssignments.filter((a) => a.projectId === projectId),
  };
}

// ─── Person pivot ────────────────────────────────────────────────────

export function buildPersonPivotRows(args: {
  members: TeamMember[];
  projects: Project[];
  forecasts: HourForecast[];
  weeks: string[];
  layout: PersonLayout;
  departments: Department[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  projectAssignments: ProjectAssignment[];
  pendingPairings: Map<string, Set<string>>;
}): GroupData[] {
  const {
    members,
    projects,
    forecasts,
    weeks,
    layout,
    departments,
    teams,
    teamAssignments,
    projectAssignments,
    pendingPairings,
  } = args;

  const projectById = new Map(projects.map((p) => [p.id, p]));
  const forecastsByMember = new Map<string, HourForecast[]>();
  for (const f of forecasts) {
    const arr = forecastsByMember.get(f.teamMemberId) || [];
    arr.push(f);
    forecastsByMember.set(f.teamMemberId, arr);
  }
  const assignedProjectsByMember = new Map<string, Set<string>>();
  for (const a of projectAssignments) {
    const s = assignedProjectsByMember.get(a.teamMemberId) || new Set<string>();
    s.add(a.projectId);
    assignedProjectsByMember.set(a.teamMemberId, s);
  }
  for (const p of projects) {
    if (p.pmId) {
      const s = assignedProjectsByMember.get(p.pmId) || new Set<string>();
      s.add(p.id);
      assignedProjectsByMember.set(p.pmId, s);
    }
    if (p.picId) {
      const s = assignedProjectsByMember.get(p.picId) || new Set<string>();
      s.add(p.id);
      assignedProjectsByMember.set(p.picId, s);
    }
  }

  const weekSet = new Set(weeks);

  function buildParent(m: TeamMember): ParentRowData {
    // Scoped to the VISIBLE horizon (D5 — sparse rows: real assignments +
    // rows with hours IN VIEW, never a stale pairing from months ago kept
    // alive forever by one old forecast). Assignment-driven pairings below
    // stay horizon-independent — an assignment is real regardless of when.
    const own = (forecastsByMember.get(m.id) || []).filter((f) => {
      const day = f.dayDate || f.weekStartDate;
      return !!day && weekSet.has(mondayOf(day));
    });
    const manual = own.filter((f) => f.sourceKind !== 'vacation');
    const vacation = own.filter((f) => f.sourceKind === 'vacation');

    const pairIds = new Set<string>(assignedProjectsByMember.get(m.id) || []);
    for (const f of manual) if (f.projectId) pairIds.add(f.projectId);
    for (const pid of pendingPairings.get(m.id) || []) pairIds.add(pid);

    const pairings: PairingRow[] = [];
    for (const projectId of pairIds) {
      const project = projectById.get(projectId);
      const rowsForPair = manual.filter((f) => f.projectId === projectId);
      const hasAnyHours = rowsForPair.some((f) => (Number(f.hours) || 0) > 0);
      pairings.push({
        id: `${m.id}::${projectId}`,
        memberId: m.id,
        projectId,
        label: project?.name || 'Unknown project',
        hint: project?.projectNumber,
        color: projectColor(projectId),
        isVacation: false,
        unforecasted: !hasAnyHours,
        editable: true,
      });
    }
    pairings.sort((a, b) => a.label.localeCompare(b.label));

    if (vacation.length > 0) {
      pairings.push({
        id: `${m.id}::vacation`,
        memberId: m.id,
        projectId: undefined,
        label: 'Vacation',
        color: VACATION_COLOR,
        isVacation: true,
        unforecasted: false,
        editable: false,
      });
    }

    return {
      id: `member:${m.id}`,
      kind: 'member',
      refId: m.id,
      label: m.name,
      hint: `${initialsOf(m.name)}`,
      pairings,
    };
  }

  if (layout === 'flat') {
    return [{ id: '__all', label: '', parents: members.map(buildParent) }];
  }

  if (layout === 'team') {
    const memberToTeams = new Map<string, string[]>();
    for (const a of teamAssignments) {
      const arr = memberToTeams.get(a.teamMemberId) || [];
      arr.push(a.teamId);
      memberToTeams.set(a.teamMemberId, arr);
    }
    const byTeam = new Map<string, TeamMember[]>();
    const noTeam: TeamMember[] = [];
    for (const m of members) {
      const ts = memberToTeams.get(m.id);
      if (!ts || ts.length === 0) {
        noTeam.push(m);
        continue;
      }
      for (const tid of ts) {
        const arr = byTeam.get(tid) || [];
        arr.push(m);
        byTeam.set(tid, arr);
      }
    }
    const out: GroupData[] = [];
    for (const t of teams) {
      const ms = byTeam.get(t.id);
      if (ms && ms.length) {
        out.push({
          id: `team:${t.id}`,
          label: t.name,
          color: t.color,
          parents: ms
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(buildParent),
        });
      }
    }
    if (noTeam.length)
      out.push({ id: '__noteam', label: 'No team', parents: noTeam.map(buildParent) });
    return out;
  }

  // department (default)
  const byDept = new Map<string, TeamMember[]>();
  const noDept: TeamMember[] = [];
  for (const m of members) {
    if (m.departmentId) {
      const arr = byDept.get(m.departmentId) || [];
      arr.push(m);
      byDept.set(m.departmentId, arr);
    } else {
      noDept.push(m);
    }
  }
  const out: GroupData[] = [];
  for (const d of departments) {
    const ms = byDept.get(d.id);
    if (ms && ms.length) {
      out.push({
        id: `dept:${d.id}`,
        label: d.name,
        color: d.color,
        parents: ms
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(buildParent),
      });
    }
  }
  if (noDept.length)
    out.push({ id: '__nodept', label: 'Unassigned', parents: noDept.map(buildParent) });
  return out;
}

// ─── Project pivot ───────────────────────────────────────────────────

export function buildProjectPivotRows(args: {
  members: TeamMember[];
  projects: Project[];
  forecasts: HourForecast[];
  weeks: string[];
  breakdown: ProjectBreakdown;
  teams: Team[];
  teamAssignments: TeamAssignment[];
  projectAssignments: ProjectAssignment[];
  departments: Department[];
}): GroupData[] {
  const {
    members,
    projects,
    forecasts,
    breakdown,
    teams,
    teamAssignments,
    projectAssignments,
    departments,
  } = args;

  const memberById = new Map(members.map((m) => [m.id, m]));
  const teamById = new Map(teams.map((t) => [t.id, t]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  const teamsByMember = new Map<string, Set<string>>();
  for (const a of teamAssignments) {
    const s = teamsByMember.get(a.teamMemberId) || new Set<string>();
    s.add(a.teamId);
    teamsByMember.set(a.teamMemberId, s);
  }

  const visibleMemberIds = new Set(members.map((m) => m.id));
  const forecastsByProject = new Map<string, HourForecast[]>();
  for (const f of forecasts) {
    if (!f.projectId || f.sourceKind === 'vacation') continue;
    if (!visibleMemberIds.has(f.teamMemberId)) continue;
    const arr = forecastsByProject.get(f.projectId) || [];
    arr.push(f);
    forecastsByProject.set(f.projectId, arr);
  }
  const assignedByProject = new Map<string, Set<string>>();
  const ensureAssign = (pid: string) => {
    let s = assignedByProject.get(pid);
    if (!s) {
      s = new Set();
      assignedByProject.set(pid, s);
    }
    return s;
  };
  for (const a of projectAssignments) {
    if (visibleMemberIds.has(a.teamMemberId)) ensureAssign(a.projectId).add(a.teamMemberId);
  }
  for (const p of projects) {
    if (p.pmId && visibleMemberIds.has(p.pmId)) ensureAssign(p.id).add(p.pmId);
    if (p.picId && visibleMemberIds.has(p.picId)) ensureAssign(p.id).add(p.picId);
  }

  const parents: ParentRowData[] = [];
  for (const project of projects) {
    const rows = forecastsByProject.get(project.id) || [];
    const assigned = assignedByProject.get(project.id) || new Set<string>();
    if (rows.length === 0 && assigned.size === 0) continue;

    const byUnit = new Map<string, { pairing: PairingRow; rows: HourForecast[] }>();
    const ensureUnit = (pairing: PairingRow) => {
      let bucket = byUnit.get(pairing.id);
      if (!bucket) {
        bucket = { pairing, rows: [] };
        byUnit.set(pairing.id, bucket);
      }
      return bucket;
    };

    for (const f of rows) {
      const member = memberById.get(f.teamMemberId);
      if (!member) continue;
      if (breakdown === 'individual') {
        ensureUnit({
          id: `${project.id}::${member.id}`,
          memberId: member.id,
          projectId: project.id,
          label: member.name,
          hint: project.pmId === member.id ? 'PM' : project.picId === member.id ? 'PIC' : undefined,
          color: 'var(--ac)',
          isVacation: false,
          unforecasted: false,
          editable: true,
        }).rows.push(f);
      } else if (breakdown === 'team') {
        const myTeams = teamsByMember.get(member.id);
        if (!myTeams || myTeams.size === 0) {
          ensureUnit({
            id: `${project.id}::__noteam`,
            label: 'No team',
            color: 'var(--bd)',
            isVacation: false,
            unforecasted: false,
            editable: false,
          }).rows.push(f);
        } else {
          for (const tid of myTeams) {
            const t = teamById.get(tid);
            if (!t) continue;
            ensureUnit({
              id: `${project.id}::team:${t.id}`,
              label: t.name,
              hint: t.shortCode,
              color: t.color || 'var(--ac)',
              isVacation: false,
              unforecasted: false,
              editable: false,
            }).rows.push(f);
          }
        }
      } else {
        if (!member.departmentId) {
          ensureUnit({
            id: `${project.id}::__nodept`,
            label: 'No department',
            color: 'var(--bd)',
            isVacation: false,
            unforecasted: false,
            editable: false,
          }).rows.push(f);
        } else {
          const d = deptById.get(member.departmentId);
          ensureUnit({
            id: `${project.id}::dept:${member.departmentId}`,
            label: d?.name || 'Unknown department',
            hint: d?.abbreviation,
            color: d?.color || 'var(--ac)',
            isVacation: false,
            unforecasted: false,
            editable: false,
          }).rows.push(f);
        }
      }
    }

    // Unforecasted individuals — only meaningful in breakdown='individual'.
    if (breakdown === 'individual') {
      const forecastedIds = new Set(rows.map((f) => f.teamMemberId));
      for (const memberId of assigned) {
        if (forecastedIds.has(memberId)) continue;
        const member = memberById.get(memberId);
        if (!member) continue;
        ensureUnit({
          id: `${project.id}::${member.id}`,
          memberId: member.id,
          projectId: project.id,
          label: member.name,
          hint: project.pmId === member.id ? 'PM' : project.picId === member.id ? 'PIC' : undefined,
          color: 'var(--ac)',
          isVacation: false,
          unforecasted: true,
          editable: true,
        });
      }
    }

    const pairings = Array.from(byUnit.values())
      .map((b) => b.pairing)
      .sort((a, b) => {
        if (!!a.unforecasted !== !!b.unforecasted) return a.unforecasted ? 1 : -1;
        return a.label.localeCompare(b.label);
      });

    parents.push({
      id: `project:${project.id}`,
      kind: 'project',
      refId: project.id,
      label: project.name,
      hint: project.projectNumber,
      color: projectColor(project.id),
      pairings,
    });
  }

  return [{ id: '__all', label: '', parents }];
}

export { bucketByWeek, sumHours };
